import { z, ZodSchema } from 'zod';

const heightError = "Please enter a valid height between 120 and 240";
const weightError = "Please enter a valid weight between 40 and 160";

export interface GetCycleResponseItem {
    id: string;
    patient: string;
    birth: string;
    created: string;
    updated: string;
    amh: number;
    fsh: number;
    afc_left: number;
    afc_right: number;
    height: number;
    weight: number;
    diagnosis: string;
}

export enum OocyteCondition {
    fresh = "fresh",
    frozen = "frozen"
}
export interface OocyteUpload {
    image: string;
    hpi: number; // hours
    condition: keyof typeof OocyteCondition;
}

export interface FormData {
    name: string;
    birth: Date;
    ethnicity: string[];
    height: number;
    weight: number;
    "amh.date": Date;
    "amh.value": number;
    "fsh.date": Date;
    "fsh.value": number;
    "afc.date": Date;
    "afc.left": number;
    "afc.right": number;
    "retrieval.date": Date;
    "retrieval.count": number;
    images?: OocyteUpload[];
}

export interface FormResponse {
    id: string
}

export const infertilityDiagnosisOptions = [
    { label: "Diminished ovarian reserve", value: "diminished_ovarian_reserve" },
    { label: "Endometriosis", value: "endometriosis" },
    { label: "Fallopian tube blockage", value: "fallopian_tube_blockage" },
  ]

export const conditionOptions = [
    { label: "Polycystic Ovary Syndrome (PCOS)", value: "PCOS" },
    { lable: "Endometriosis", value: "Endometriosis" },
    { label: "Thyroid disorders", value: "Thyroid disorders" },
    { label: "Autoimmune diseases", value: "Autoimmune diseases" },
    { label: "Diabetes", value: "Diabetes" },
    { label: "Hypertension", value: "Hypertension" }, ,
    { label: "Other", value: "Other" }
];


export interface CycleData {
    cycleId: string;
    cycleDate: string,
    patientName: string;
    recommendedProtocol: string;
    predictedOocytes: number;
    oocytes: number;
    predirectedOocytes: number;
    actualOutcome: string;
}


export const schema: ZodSchema<FormData> = z.object({
    name: z.string().min(1),
    birth: z.date().max(new Date(), "Please enter a valid date of birth"),
    height: z.number().int().min(120, heightError).max(240, heightError),
    weight: z.number().int().min(40, weightError).max(160, weightError),
    ethnicity: z.array(z.string().min(1)).min(1, "Please select at least one"),
    "amh.date": z.date(),
    "amh.value": z.number(),
    "fsh.date": z.date(),
    "fsh.value": z.number(),
    "afc.date": z.date(),
    "afc.left": z.number(),
    "afc.right": z.number(),
    "retrieval.count": z.number().int().min(0, "Please enter a valid number of oocytes"),
    "retrieval.date": z.date(),
    "images": z.array(z.object({
        image: z.string(),
        hpi: z.number().int().min(0),
        condition: z.enum(Object.keys(OocyteCondition) as [keyof typeof OocyteCondition])
    })).optional()
});

export const scoretoColor = (score: number) => {
    if (score <= 20) return "#FEF08A";
    if (score <= 35) return "#FDBA74";
    if (score <= 60) return "#C4B5FD";
    return "#C1DCA0";
}


export const scoretoText = (score: number) => {
    if (score <= 20) return "Least likely to become a blastocyst ";
    if (score <= 35) return "Less likely to become a blastocyst ";
    if (score <= 60) return "Moderately likely to become blastocyst";
    return "Most likely to become a blastocyst ";
}


export enum ReviewStage {
    fertilization = 'Fertilization',
    blastulation = 'Blastulation',
    liveBirth = 'Live Birth',
}

export interface BaseReviewItem {
    id: string;
    stage: Exclude<ReviewStage, ReviewStage.liveBirth>;
    oocytes: number;
    predicted: number;
}

export interface LiveBirthReviewItem extends Omit<Omit<Omit<BaseReviewItem, 'stage'>, 'predicted'>, 'oocytes'> {
    stage: ReviewStage.liveBirth;
    successfulPregnancy: boolean;
    liveBirths: number;
}

export type ReviewItem = BaseReviewItem | LiveBirthReviewItem;